import {
  AHI_SCORE_THRESHOLD,
  APNEA_SCORE,
  FAILURE_REASON,
} from 'src/routes/IndividualDashboard/components/DashboardTab/SleepApnea/Connector';
import { defineMessages, IntlShape } from 'react-intl';
import { isNullOrUndefined } from 'src/utils/parser';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';

export const getApneaScoreValue = (
  apneaScore: APNEA_SCORE | null | undefined,
  intl: IntlShape,
) => {
  switch (apneaScore) {
    case APNEA_SCORE.ABOVE:
      return intl.formatMessage(messages.scoreAboveValue, {
        ahiScoreThreshold: AHI_SCORE_THRESHOLD,
      });
    case APNEA_SCORE.BELOW:
      return intl.formatMessage(messages.scoreBelowValue, {
        ahiScoreThreshold: AHI_SCORE_THRESHOLD,
      });
    default:
      return intl.formatMessage(messages.na);
  }
};

export const getApneaScoreDescription = (
  apneaScore: APNEA_SCORE | null | undefined,
  intl: IntlShape,
  failureReason:
    | FAILURE_REASON.NOT_ENOUGH_SLEEP
    | FAILURE_REASON.NO_DATA
    | null
    | undefined,
) => {
  switch (apneaScore) {
    case APNEA_SCORE.ABOVE:
      return intl.formatMessage(messages.scoreAboveDescription);
    case APNEA_SCORE.BELOW:
      return intl.formatMessage(messages.scoreBelowDescription);
    default:
      return intl.formatMessage(messages[failureReason ? failureReason : 'na']);
  }
};

export const getTrendLabel = (
  trend: APNEA_SCORE | 'N/A' | undefined,
  intl: IntlShape,
) => {
  switch (trend) {
    case APNEA_SCORE.ABOVE:
    case APNEA_SCORE.BELOW:
      return intl.formatMessage(messages.clearTrend);
    case APNEA_SCORE.NO_CLEAR_TREND:
      return intl.formatMessage(messages.noClearTrend);
    default:
      return intl.formatMessage(messages.na);
  }
};

export const getTrendIconName = (trend: APNEA_SCORE | 'N/A' | undefined) => {
  switch (trend) {
    case APNEA_SCORE.ABOVE:
      return 'ArrowUp';
    case APNEA_SCORE.BELOW:
      return 'ArrowDown';
    default:
      return undefined;
  }
};
export const tooltipFormatter = (
  value: ValueType | null | undefined,
  intl: IntlShape,
) => {
  if (isNullOrUndefined<ValueType | null | undefined>(value)) {
    return '';
  }
  const hoursCount = Math.floor(value);
  const minutesCount = Math.round((value - hoursCount) * 60);
  const hoursString = `${hoursCount ? intl.formatMessage(messages.hours, { hoursCount }) : ''}`;
  const minutesString = `${minutesCount ? ` ${intl.formatMessage(messages.minutes, { minutesCount })}` : ''}`;
  if (hoursCount === 0 && minutesCount === 0) {
    return intl.formatMessage(messages.minutes, { minutesCount: 0 });
  }
  return hoursString && minutesString
    ? `${hoursString} ${intl.formatMessage(messages.and)} ${minutesString}`
    : `${hoursString} ${minutesString}`;
};

export const messages = defineMessages({
  score: {
    id: 'SleepApnea.index.score',
    defaultMessage: 'Apnea Score',
  },
  scoreAboveValue: {
    id: 'SleepApnea.index.scoreAboveValue',
    defaultMessage: 'Above {ahiScoreThreshold} AHI',
  },
  scoreBelowValue: {
    id: 'SleepApnea.index.scoreBelowValue',
    defaultMessage: 'Below {ahiScoreThreshold} AHI',
  },
  scoreAboveDescription: {
    id: 'SleepApnea.index.scoreAboveDescription',
    defaultMessage: 'Moderate - Severe',
  },
  scoreBelowDescription: {
    id: 'SleepApnea.index.scoreBelowDescription',
    defaultMessage: 'Mild - No Apnea',
  },
  NOT_ENOUGH_SLEEP: {
    id: 'SleepApnea.index.NOT_ENOUGH_SLEEP',
    defaultMessage: 'Not enough sleep',
  },
  NO_SLEEP_DATA: {
    id: 'SleepApnea.index.NO_SLEEP_DATA',
    defaultMessage: 'No sleep data',
  },
  NO_DATA: {
    id: 'SleepApnea.index.NO_DATA',
    defaultMessage: 'No data',
  },
  NOT_ENOUGH_DATA: {
    id: 'SleepApnea.index.NOT_ENOUGH_DATA',
    defaultMessage: 'No enough data',
  },
  clearTrend: {
    id: 'SleepApnea.index.clearTrend',
    defaultMessage: '3 days',
  },
  noClearTrend: {
    id: 'SleepApnea.index.noClearTrend',
    defaultMessage: 'No clear trend',
  },
  na: {
    id: 'SleepApnea.index.na',
    defaultMessage: 'N/A',
  },
  hours: {
    id: 'SleepApnea.utils.hours',
    defaultMessage: '{hoursCount} {hoursCount, select, 1 {hour} other {hours}}',
  },
  minutes: {
    id: 'SleepApnea.utils.minutes',
    defaultMessage:
      '{minutesCount} {minutesCount, select, 1 {minute} other {minutes}}',
  },
  and: {
    id: 'SleepApnea.utils.and',
    defaultMessage: 'and',
  },
});
